import { msg } from '@lingui/core/macro';
// SaaS-specific config cho @cnv/ui's AppLayout (2-tier sidebar identical Core).
// Schema NAV_GROUPS clone NGUYÊN từ Core's adminMenu — đặc biệt là `chat` group + `system` group.
// SaaS-only items (API Keys, Webhooks) đặt vào `system` group với `group: 'Tích hợp', groupLabel: msg`Tích hợp`` (nhãn trùng catalog cnv-ui thì thêm context 'host').

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// MessageCircle (Inbox) / Target (CRM + Marketing) / Briefcase (Tuyển dụng) đã gỡ cùng
// 4 module bị bỏ khỏi CNV Connect — thêm lại khi bật module tương ứng.
// LayoutDashboard (Tổng quan) + LifeBuoy (Hỗ trợ) gỡ 27/08/2026 cùng hai mục menu đó —
// icon chỉ còn nằm trong ghi chú "khai lại thế này" ở NAV_GROUPS, import vào là lint đỏ.
import { MessagesSquare, Settings, FileCheck2, CheckSquare, Users, Zap, CreditCard, Wrench, Camera, Building2, CalendarDays } from 'lucide-react';
import AppLayoutShared from '@cnv/ui/layout/AppLayout';
import UserMenu from '@cnv/ui/layout/sidebar/UserMenu';
import TopBarV2 from './v2/TopBarV2';
import RailBottomV2 from './v2/RailBottomV2';
import UiVersionSwitch from '@cnv/ui/layout/UiVersionSwitch';
import { UI_V1, UI_V2, applyUiVersion, isTenantAllowed, readUiVersion, subscribeUiVersion } from '../lib/uiVersion';
import { buildRouteMap, checkRoutePermission } from '../lib/routePermission';
import { prefetchAllRoutes } from '@cnv/ui/utils/lazyWithReload';
import ApprovalsSecondaryNav from '@cnv/ui/layout/sidebar/ApprovalsSecondaryNav';
import TasksSecondaryNav from '@cnv/ui/layout/sidebar/TasksSecondaryNav';
import useSidebarBadges from '@cnv/ui/hooks/useSidebarBadges';
import useBadgeRealtime from '@cnv/ui/hooks/useBadgeRealtime';
import { useTeamChatUnreadTotal } from '@cnv/ui/lib/teamChatUnread';
import api from '@host/axios';
import { useAuthStore } from '../lib/authStore';

// Permission gate helper — item.permission='storage:view_all' → user phải có
// permission đó trong currentUser.permissions hoặc là admin (org_role mapped).
// Empty / null permission → ai cũng thấy.
function userHasPermission(user, perm) {
  if (!perm) return true;
  if (user?.role === 'admin') return true;
  const perms = user?.permissions || [];
  if (perms.includes('*')) return true;
  return perms.includes(perm);
}

// Sidebar badge endpoints — pure data signals fetched mỗi 30s (poll) + tab focus + manual trigger.
// Khóa = menu group key trong NAV_GROUPS. Khớp Core's SIDEBAR_BADGE_ENDPOINTS để
// chung 1 nguồn @cnv/ui/hooks/useSidebarBadges (shared kernel).
// `team_chat` KHÔNG dùng polling — qua STOMP store useTeamChatUnreadTotal (realtime).
// TẤT CẢ trỏ 1 endpoint gộp /badges/summary → useSidebarBadges dedup theo url → 1 request/poll
// thay vì 5 (xem modules/badges + useSidebarBadges dedup).
const SIDEBAR_BADGE_ENDPOINTS = {
  tasks:       { url: '/badges/summary', pick: (r) => r.data?.data?.tasks || 0 },
  approvals:   { url: '/badges/summary', pick: (r) => r.data?.data?.approvals || 0 },
  // Badge `support` gỡ 27/08/2026 cùng cụm Hỗ trợ (xem khối ghi chú ở NAV_GROUPS): không còn
  // nhóm menu nào đọc nó, giữ lại chỉ tổ đếm ticket mỗi vòng poll cho một chỗ không ai thấy.
  // Badge `chat` / `chat-inbox` / `chat-ai-assist` / `recruitment` gỡ cùng 4 module bị bỏ
  // khỏi CNV Connect (xem khối MODULE bị bỏ bên dưới) — badge của group không tồn tại thì
  // không ai đọc, mà `chat-ai-assist` còn tốn 1 request riêng mỗi vòng poll.
  // File xuất XONG mà mình chưa tải lần nào — thiết kế gốc muốn "chấm đỏ ở lối tắt cạnh chuông",
  // nhưng layout SaaS không có thanh trên cùng nên báo ở badge nhóm Tiện ích (cùng đường badge sẵn có).
  utilities: { url: '/badges/summary', pick: (r) => r.data?.data?.exports || 0 },
};

// Custom secondary nav per group key — match Core SidebarSecondary.jsx CUSTOM_SECONDARY_NAVS:
// - approvals: 12+ filter items (tab tasks/sent)
// - tasks: filter mine/created/assigned/following + project tree + stage filters
const CUSTOM_SECONDARY = {
  approvals: ApprovalsSecondaryNav,
  tasks: TasksSecondaryNav,
};

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE BỊ BỎ KHỎI CNV CONNECT — sếp Phú chốt 2026-08-13
//
//   Inbox (`chat`, /inbox)  ·  CRM (`crm`, /crm)  ·  Marketing (/marketing)
//   Tuyển dụng (`recruitment`, /recruitment)
//
// CNV Connect bán ở Canada / Mỹ Latinh với phạm vi HR + Vận hành; 4 cụm trên thuộc
// CNV Work. Cách bỏ = ẩn menu + chặn route (App.jsx redirect về /dashboard); code FE,
// module BE, bảng DB GIỮ NGUYÊN để bật lại chỉ bằng 1 commit revert.
//
// LƯU Ý khi sửa file này: 4 group đó vẫn CÓ trong Core adminMenu, nên
// tools/check-port-parity.js sẽ báo drift nếu không khai vào CONNECT_REMOVED_GROUPS.
// Lý do bỏ ghi ở docs/PORT_GAPS.md.
//
// ĐỢT 27/08/2026 — sếp Phú chốt ẩn tiếp, CÙNG CÁCH LÀM (menu + route, giữ code/BE/DB):
//   Tổng quan (`dashboard`, /dashboard — CHỈ ẩn menu, route phải sống, xem ghi chú dưới)
//   Hỗ trợ (`support`, /tickets/*)  ·  Sự kiện (/employees/events)  ·  Phạt đi trễ (/penalties)
// Ba cụm sau KHÔNG cần khai CONNECT_REMOVED_GROUPS: audit chỉ soi section 'chat' + 'crm'.
//
// KHÔNG bỏ theo: «Chat» (team_chat) là chat nội bộ, khác Inbox khách hàng; «Thư viện
// Pipeline» + «Kho in ấn» nằm ở Tiện ích và ticket vẫn dùng; «Chi phí AI» ở Hệ thống.
// ═══════════════════════════════════════════════════════════════════════════════

const NAV_GROUPS = [
  // ===== Tổng quan (Overview) — ẨN KHỎI MENU 27/08/2026, sếp Phú chốt =====
  // Core vẫn có (adminMenu line 19-24, key='dashboard'), Connect chỉ bỏ MỤC MENU.
  //
  // ⛔ KHÔNG gỡ route `/dashboard` trong App.jsx và KHÔNG bỏ nó khỏi ALWAYS_ALLOWED:
  // đó là ĐÍCH RƠI của mọi đường thoát trong cụm này — guard quyền ở cuối file
  // (`if (!allowed) return <Navigate to="/dashboard">`), `<Route index>`, LoginPage, và
  // ~10 route của module đã bỏ đều trỏ về đó. Trỏ chúng sang một trang CÓ gate quyền là
  // tự tay tạo vòng lặp chuyển hướng cho user thường (trang chặn → /X → chặn → /X…).
  // Vì vậy trang vẫn sống và vẫn là nơi đáp sau đăng nhập; chỉ không còn dòng menu.
  // Muốn đổi luôn nơi đáp thì phải chọn một trang KHÔNG gate (ALWAYS_ALLOWED) làm đích mới.
  //
  // Ghi chú cũ giữ lại cho người sau: mục này từng là submenu một-mục; 21/08 cnv-ui gỡ khoá
  // con 'dashboard-overview' khỏi FINE_KEY_MAP + KEY_TO_MODULE nên `permission` phải nằm ở
  // chính nhóm. Bật lại menu thì khai lại đúng dạng link đơn dưới đây:
  //   { key: 'dashboard', module: 'dashboard', label: msg`Tổng quan`, icon: LayoutDashboard,
  //     path: '/dashboard', permission: 'dashboard_overview:view' },

  // ===== Nhân sự (Employees) — clone 1:1 Core adminMenu (key='employees') — 27 items =====
  // Sub-group via group: Nhân sự / Hợp đồng / Lương / Chấm công / Nghỉ phép / Truyền thông / Tài sản / Cài đặt
  {
    key: 'employees',
    module: 'employees',
    label: msg({ context: 'host', message: `Nhân sự` }),
    icon: Users,
    basePath: '/employees',
    items: [
      { key: 'employees-list', label: msg({ context: 'host', message: `Nhân viên` }), path: '/employees', group: 'Nhân sự', groupLabel: msg({ context: 'host', message: `Nhân sự` }), permission: 'employees_list:view' },
      { key: 'employees-invitations', label: msg`Lời mời nhân viên`, path: '/users/invitations', group: 'Nhân sự', groupLabel: msg({ context: 'host', message: `Nhân sự` }), permission: 'employees_invitations:view' },
      // "Sự kiện" (/employees/events) ẨN 27/08/2026 — sếp Phú chốt. Core vẫn có; Connect bỏ
      // mục menu + App.jsx chuyển route về /dashboard. Page cnv-ui + BE giữ nguyên:
      //   { key: 'employees-events', label: msg`Sự kiện`, path: '/employees/events', group: 'Nhân sự', groupLabel: msg({ context: 'host', message: `Nhân sự` }), permission: 'employees_events:view' },
      { key: 'teams', label: msg({ context: 'host', message: `Team / Phòng ban` }), path: '/teams', group: 'Nhân sự', groupLabel: msg({ context: 'host', message: `Nhân sự` }), permission: 'teams:view' },
      { key: 'org-chart', label: msg`Sơ đồ tổ chức`, path: '/org-chart', group: 'Nhân sự', groupLabel: msg({ context: 'host', message: `Nhân sự` }), permission: 'org_chart:view' },
      { key: 'employees-contracts', label: msg({ context: 'host', message: `Hợp đồng` }), path: '/employees/contracts', group: 'Hợp đồng', groupLabel: msg({ context: 'host', message: `Hợp đồng` }), permission: 'employees_contracts:view' },
      { key: 'employees-contract-types', label: msg({ context: 'host', message: `Loại hợp đồng` }), path: '/employees/contract-types', group: 'Hợp đồng', groupLabel: msg({ context: 'host', message: `Hợp đồng` }), permission: 'employees_contract_types:view' },
      { key: 'employees-job-titles', label: msg({ context: 'host', message: `Danh mục chức vụ` }), path: '/employees/job-titles', group: 'Hợp đồng', groupLabel: msg({ context: 'host', message: `Hợp đồng` }), permission: 'employees_contracts:view' },
      // Đơn giá giờ công — mục DUY NHẤT của group "Lương" đang bật. Đặt ở đây chứ không đẻ
      // mục menu mới: đây là lương của MỘT NGƯỜI, cùng họ với hợp đồng và chức vụ ngay trên.
      // Chỗ TIÊU con số này là Dự án ▸ Lỗ lãi — hai màn vì hai câu hỏi, hai nhóm người đọc.
      // Quyền `reports:view` khớp `costRateMW` ở máy chủ. Core + Work KHÔNG có mục này ⇒ drift
      // CÓ CHỦ Ý (endpoint chỉ có ở Connect), ghi ở docs/PORT_GAPS.md.
      { key: 'employees-cost-rates', label: msg`Đơn giá giờ công`, path: '/employees/cost-rates', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }), permission: 'reports:view' },
      // Các mục Lương còn lại tạm ẩn — tính năng chưa hoàn chỉnh. Bật lại khi port full payroll module.
      // { key: 'payroll-periods', label: msg`Bảng lương`, path: '/payroll/periods', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }) },
      // { key: 'payroll-structures', label: msg`Khung lương NV`, path: '/payroll/structures', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }) },
      // { key: 'payroll-levels', label: msg`Bậc cấp`, path: '/payroll/levels', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }) },
      // { key: 'payroll-insurance', label: msg`BHXH/BHYT/BHTN`, path: '/payroll/insurance', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }) },
      // { key: 'payroll-tax', label: msg`Thuế TNCN`, path: '/payroll/tax', group: 'Lương', groupLabel: msg({ context: 'host', message: `Lương` }) },
      { key: 'attendance-list', label: msg`Quản lý chấm công`, path: '/attendance', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'attendance_list:view' },
      { key: 'timesheet', label: msg`Bảng chấm công (tuần)`, path: '/timesheet', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'timesheet:view' },
      { key: 'timesheet-monthly', label: msg`Bảng chấm công (tháng)`, path: '/timesheet/monthly', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'timesheet_monthly:view' },
      // «Ca làm việc» (mẫu ca HR, bảng `shifts`) ẨN 30/09/2026 — §7.1 docs/BAN_GIAO_VIEC_VA_CA.md. Từ «ca = việc»
      // (24/09) CA là việc trên bàn Dự án › Xếp việc; mục HR cùng tên làm người dùng tưởng là một (sếp Phú: «tính năng
      // ca là gì nữa, sao rối điên vậy»). Đo prod 30/09: Sky 0 mẫu ca. Route + page + BE GIỮ (link cũ vẫn mở được,
      // «Quy tắc tính công» ngay dưới vẫn dùng) — chỉ gỡ khỏi menu. Core + Work giữ mục này.
      //   { key: 'shifts', label: msg`Ca làm việc`, path: '/shifts', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'shifts:view' },
      { key: 'shifts-credit-rules', label: msg`Quy tắc tính công`, path: '/shifts/credit-rules', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'shifts:view' },
      // M7 — «Lịch làm việc» (phân ca HR, bảng `shift_rosters`) ẨN 30/09/2026 — cùng lý do «Ca làm việc» ngay trên. Dữ
      // liệu của Sky ở đây là BẢN CHIẾU Connecteam (45.334 dòng, không mẫu ca) của chính các ca đã nằm trên bàn Xếp việc —
      // hai màn cho một thứ, và bản này đứng im khi Connecteam tắt. Route + page + BE GIỮ, chỉ gỡ khỏi menu. Mục này vốn
      // KHÔNG có ở Core (ADR-036).
      //   { key: 'attendance-roster', label: msg`Lịch làm việc`, path: '/attendance/roster', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'attendance_list:view' },
      { key: 'corrections', label: msg({ context: 'host', message: `Duyệt điều chỉnh` }), path: '/corrections', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'corrections:view' },
      // Giờ hở (D5 của docs/CHUAN_GIO_CONG.md) — phần giờ CÓ MẶT không quy về việc nào. Đặt
      // ở cụm Chấm công vì đó là câu hỏi về chấm công, và người mở nó ra đang nhìn bảng công.
      // Quyền dùng lại `attendance_list:view`, KHÔNG đẻ khoá mới. Core + Work chưa có endpoint
      // này ⇒ drift CÓ CHỦ Ý, ghi ở docs/PORT_GAPS.md.
      { key: 'unallocated-hours', label: msg`Giờ hở`, path: '/attendance/unallocated-hours', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'attendance_list:view' },
      // N6 — Cài đặt chấm công. Mục này KHÔNG có ở Core: cụm chấm công Connect rẽ nhánh
      // (ADR-036), đã ghi vào docs/PORT_GAPS.md.
      // Đã kiểm: `node tools/check-port-parity.js` KHÔNG báo lệch vì nó chỉ đối chiếu
      // Core→SaaS (mục Core có mà SaaS thiếu), không soi mục SaaS thừa ra. Nên ALLOWED_SKIPS
      // không cần thêm gì — ghi rõ ở đây để lần sau không ai đi thêm một mục thừa vào đó.
      // Quyền dùng lại attendance_list (không đẻ khoá quyền mới — xem header policiesV1.js).
      { key: 'attendance-policies', label: msg`Cài đặt chấm công`, path: '/attendance/policies', group: 'Chấm công', groupLabel: msg`Chấm công`, permission: 'attendance_list:view' },
      // M8 — Biểu mẫu hiện trường. NHÓM RIÊNG, không nhét vào "Chấm công" (sếp Phú chốt
      // 16/08/2026). Lý do: soạn mẫu và tra phiếu đã nộp là HAI VIỆC của HAI người, nhịp
      // Nhóm "Biểu mẫu hiện trường" (Mẫu phiếu · Phiếu đã nộp) GỠ 20/08/2026 — ADR-037.
      // Phiếu nay thuộc cụm VIỆC: soạn ngay trong dự án, nộp kèm lượt check-in việc. Cụm chấm
      // công không còn khái niệm phiếu, nên hai mục này không còn chỗ đứng.
      { key: 'leaves', label: msg({ context: 'host', message: `Nghỉ phép` }), path: '/leaves', group: 'Nghỉ phép', groupLabel: msg({ context: 'host', message: `Nghỉ phép` }), permission: 'leaves:view' },
      { key: 'leave-balance', label: msg`Phép năm`, path: '/leave-balance', group: 'Nghỉ phép', groupLabel: msg({ context: 'host', message: `Nghỉ phép` }), permission: 'leave_balance:view' },
      { key: 'holidays', label: msg`Ngày nghỉ lễ`, path: '/holidays', group: 'Nghỉ phép', groupLabel: msg({ context: 'host', message: `Nghỉ phép` }), permission: 'holidays:view' },
      { key: 'assets-list', label: msg`Danh sách tài sản`, path: '/assets', group: 'Tài sản', groupLabel: msg`Tài sản`, permission: 'assets:view' },
      { key: 'assets-assignments', label: msg({ context: 'host', message: `Giao & Thu hồi` }), path: '/assets/assignments', group: 'Tài sản', groupLabel: msg`Tài sản`, permission: 'asset_assignments:view' },
      { key: 'assets-audit', label: msg`Kiểm kê`, path: '/assets/audit', group: 'Tài sản', groupLabel: msg`Tài sản`, permission: 'asset_audit:view' },
      { key: 'digital-assets-list', label: msg`Danh sách`, path: '/digital-assets', group: 'Tài sản số', groupLabel: msg`Tài sản số`, permission: 'digital_assets_list:view' },
      { key: 'digital-assets-billing', label: msg`Theo dõi phí`, path: '/digital-assets/billing', group: 'Tài sản số', groupLabel: msg`Tài sản số`, permission: 'digital_assets_billing:view' },
      { key: 'digital-assets-alerts', label: msg({ context: 'host', message: `Cảnh báo` }), path: '/digital-assets/alerts', group: 'Tài sản số', groupLabel: msg`Tài sản số`, permission: 'digital_assets_alerts:view' },
      // "Phạt đi trễ" (/penalties) ẨN 27/08/2026 — sếp Phú chốt. Cả cụm: mục menu + route.
      // Chỉ mục NÀY đi, "Địa điểm" ở lại — nhóm con "Cài đặt" vẫn còn nên vẫn hiện đầu mục.
      //   { key: 'penalties', label: msg`Phạt đi trễ`, path: '/penalties', group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'penalties:view' },
      { key: 'locations', label: msg({ context: 'host', message: `Địa điểm` }), path: '/locations', group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'locations:view' },
    ],
  },

  // ===== Chat (Slack/Lark-style) — clone 1:1 từ attendance-app =====
  // Core: { key:'team_chat', label:msg`Chat`, icon:MessagesSquare, path:'/chat' } — direct
  // path, KHÔNG có submenu/items[]. Gate `privateModule` (SaaS) = `private:true` (Core).
  {
    key: 'team_chat',
    module: 'team_chat',
    label: msg`Chat`,
    icon: MessagesSquare,
    path: '/chat',
    privateModule: 'team_chat',
  },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Thứ tự nhóm dưới đây ĐỒNG NHẤT với Core adminMenu.js — 2026-05-23.
  // Khi sửa Core thì sync SaaS giữ thống nhất UX.
  // Order: support → tasks → approvals → marketing → utilities → reports →
  //        recruitment → automation → (finance/CNV-only) → system → marketplace
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // ===== Hỗ trợ (Ticket/Help desk) — TẮT 27/08/2026, sếp Phú chốt "chưa cần tính năng này".
  // Cùng cách với 4 module bỏ ở khối trên: gỡ mục menu + App.jsx chuyển mọi /tickets/* về
  // /dashboard; code FE (page cnv-ui), module BE `support`, bảng DB GIỮ NGUYÊN — bật lại chỉ
  // bằng 1 commit revert. Khoá quyền support_* cũng giữ (Core + Work SaaS vẫn dùng chung).
  //
  // Đi cùng lần tắt này, đừng bật lại lẻ tẻ mà quên:
  //   · badge `support` trong SIDEBAR_BADGE_ENDPOINTS (đầu file) — mỗi vòng poll đọc số ticket
  //   · 2 mẫu /tickets/:id + /support/:id trong FULLSCREEN_PATTERNS (cuối file)
  //   · mục 'tickets' trong CONNECT_COMMANDS (App.jsx) — Cmd+K là lối vào thứ hai của cụm này
  //   · 9 route + 9 import lazy page support trong App.jsx
  // Khai lại nguyên khối:
  //   { key: 'support', module: 'support', label: msg`Hỗ trợ`, icon: LifeBuoy, basePath: '/tickets',
  //     items: [ Ticket · Dashboard · Loại hỗ trợ · SLA · Phân công · Mẫu phản hồi · Widget · Cài đặt ticket ] }
  // (canonical path /tickets/* — anh chốt 2026-05-17; BE folder vẫn tên "support".)

  // ===== Dự án — TỔNG QUAN toàn bộ dự án (27/08/2026, sếp Phú duyệt) =====
  // Nhóm LINK ĐƠN, đặt ngay trước «Công việc»: dự án là vật chứa, việc nằm bên trong.
  //
  // Vì sao KHÔNG nhét thành mục con của nhóm «Công việc»: nhóm đó dùng nav phụ TUỲ BIẾN
  // (CUSTOM_SECONDARY.tasks = TasksSecondaryNav, ở cnv-ui) nên `items[]` của nó KHÔNG được
  // vẽ ra — thêm mục vào đó là thêm một dòng không ai nhìn thấy.
  //
  // Quyền dùng lại `tasks:view`, KHÔNG đẻ khoá mới: khoá mới phải seed cả ở cnv-ui (gói dùng
  // chung ba hệ) — cùng lý do đã ghi cho `attendance-policies` và cụm Ảnh.
  // Core + Work SaaS KHÔNG có nhóm này ⇒ drift CÓ CHỦ Ý, ghi ở docs/PORT_GAPS.md.
  {
    key: 'projects',
    module: 'tasks',
    label: msg({ context: 'host', message: `Dự án` }),
    icon: Building2,
    basePath: '/projects',
    items: [
      { key: 'projects-list',     label: msg`Danh sách`,      path: '/projects',       permission: 'tasks:view' },
      { key: 'projects-board',    label: msg`Bảng giai đoạn`, path: '/projects/board', permission: 'tasks:view' },
      { key: 'projects-map',      label: msg`Bản đồ`,         path: '/projects/map',   permission: 'tasks:view' },
      // Bản đồ CHECK-IN — chuyển từ Kho báo cáo về đây 27/08/2026 (sếp Phú). Hai bản đồ là hai
      // câu hỏi khác nhau: «Bản đồ» = công trình nằm ở đâu · «Bản đồ check-in» = người đang ở
      // đâu. Thẻ cũ trong Kho báo cáo đã ẩn (HIDDEN_REPORT_KEYS ở App.jsx) để một trang chỉ có
      // MỘT đường vào.
      // URL RIÊNG từ 04/09/2026 (sếp Phú: *"checkin là làm url riêng đi"*). Bản 31/08 trỏ
      // vào `/projects/map?layer=checkin` — một mục menu nhắm vào query string của mục menu
      // ngay trên nó. Đường cũ vẫn sống: kernel ProjectsMapPage chuyển hướng về path này.
      { key: 'projects-checkin-map', label: msg`Bản đồ check-in`, path: '/projects/checkin-map', permission: 'tasks:view' },
      // Lỗ lãi — quyền `reports:view`, KHÔNG `tasks:view` như bốn mục trên: nó quy ngược ra
      // lương người thật từ giờ công. Máy chủ đã chặn (`costRateMW` ở checkinV1.js); mục menu
      // phải khớp, nếu không người quản lý việc thấy mục rồi bấm vào ăn 403 không hiểu vì sao.
      // Cài đặt áp cho MỌI dự án (thời gian làm việc tự đóng phiên). Đặt Ở CỤM chứ
      // không trong ngăn kéo một dự án — sếp Phú 31/08: cài đặt cả công ty mà nằm trong
      // setting của một dự án thì người sửa tưởng chỉ đổi cho dự án đang mở. Cùng nếp với
      // «Cài đặt chấm công» của cụm Chấm công. Quyền `tasks:view` như các mục khác của cụm.
      // Hai khuôn dùng lại — rút khỏi dãy ngang 03/09 (nó đã 7 ô và lặp lại menu này).
      { key: 'projects-templates',   label: msg({ context: 'host', message: `Mẫu dự án` }),       path: '/projects/templates',      permission: 'tasks:view' },
      { key: 'projects-form-templates', label: msg`Mẫu biểu mẫu`, path: '/projects/form-templates', permission: 'tasks:view' },
      // «Xếp việc» ĐÃ RA nhóm cấp 1 riêng (sếp Phú 16/09/2026) — xem khối ngay dưới nhóm này.
      // Giữ MỘT đường vào: đừng khai lại ở đây, hai lối vào cùng một trang là chỗ người dùng
      // bắt đầu hỏi "hai cái này khác gì nhau".
      // «Cài đặt» ĐỨNG CUỐI (sếp Phú 04/09/2026). Nó là chỗ chỉnh cấu hình cho cả cụm, không
      // phải một màn để xem — xếp giữa các màn nghiệp vụ thì mắt vướng vào nó mỗi lần dò menu.
      // ⚠️ Thứ tự này phải KHỚP Core (`attendance-app/admin/src/navigation/adminMenu.js`),
      // `tools/check-port-parity.js` gác — đổi một bên là gác đỏ.
      { key: 'projects-settings', label: msg`Cài đặt`, path: '/projects/settings', permission: 'tasks:view' },
    ],
  },

  // ===== Xếp việc — NHÓM CẤP 1, link đơn (sếp Phú 16/09/2026: "đưa Scheduling ra menu chính")
  // Trước đó nó là mục con thứ 7 của «Dự án». Người xếp lịch mở màn này mỗi ngày, nhiều lần —
  // mà mỗi lần phải bung một nhóm rồi dò trong bảy dòng. Connecteam để Scheduling ngang hàng
  // với Projects trên thanh chính; đây cũng vậy, và đặt NGAY SAU «Dự án» cho đúng mạch đọc:
  // dự án (ở đâu) → xếp việc (ai, hôm nào) → công việc (làm gì).
  //
  // Quyền RIÊNG «Xếp việc» (30/09/2026) — module `work_schedule`, dòng riêng trên màn Phân quyền. MẶC ĐỊNH AI CŨNG
  // THẤY mục này (sếp Phú 30/09 tối: «Menu xếp việc là mặc định phải show all» — mẫu `employee` có ô Xem, migration
  // 1796500000000): người chỉ Xem mở bàn chỉ đọc, thấy lịch ĐÃ GỬI; ca nháp / tiền công là của người có ô Sửa (máy chủ
  // lọc, đầu api/src/modules/tasks/routes/workScheduleV1.js). Vài giờ trước đó (1796300000000) mẫu employee không có
  // ô nào ⇒ 453/454 người ở Sky mất mục menu. Trước 30/09: `tasks:view` ⇒ ai cũng mở bàn, thấy cả ca nháp.
  //
  // ⛔ CHỈ SITE CONNECT (sếp Phú chốt lại 16/09/2026: *"chỉ áp dụng riêng cho siteconnect"*).
  // Core + Work SaaS GIỮ NGUYÊN «Xếp việc» làm mục con của «Dự án» — đừng "đồng bộ" hộ.
  // Lý do khách quan: Sky xếp lịch cho 125 thợ mỗi ngày nên đây là màn mở nhiều nhất của họ;
  // hai hệ kia cụm Dự án dùng nhẹ hơn, đẩy lên cấp 1 chỉ làm dài thanh menu. Drift CÓ CHỦ Ý,
  // ghi ở docs/PORT_GAPS.md. `tools/check-port-parity.js` không soi nhóm này nên nó không đỏ —
  // càng phải ghi rõ ở đây, kẻo lần port sau có người "sửa cho khớp Core".
  {
    key: 'work-schedule',
    module: 'work_schedule',
    label: msg`Xếp việc`,
    icon: CalendarDays,
    path: '/schedule',
    permission: 'work_schedule:view',
  },

  // ===== Công việc (Tasks) — clone 1:1 từ Core adminMenu (key='tasks') =====
  // Standard menu items minimal (1 item) — TasksSecondaryNav custom 240px sidebar render full UI
  {
    key: 'tasks',
    module: 'tasks',
    label: msg`Công việc`,
    icon: CheckSquare,
    basePath: '/tasks',
    items: [
      { key: 'tasks-home', label: msg({ context: 'host', message: `Nhiệm vụ` }), path: '/tasks', permission: 'tasks:view' },
      // Nhịp làm việc kiểu sprint. Core chưa có — khai ở tools/check-port-parity.js
      // ALLOWED_EXTRAS + docs/PORT_GAPS.md.
      { key: 'tasks-circles', label: msg`Circle`, path: '/tasks/circles', permission: 'tasks:view' },
    ],
  },

  // ===== Phê duyệt (Approvals) — clone 1:1 từ Core adminMenu (key='approvals') =====
  {
    key: 'approvals',
    module: 'approvals',
    label: msg({ context: 'host', message: `Phê duyệt` }),
    icon: FileCheck2,
    basePath: '/approvals',
    items: [
      { key: 'approvals-inbox', label: msg`Hộp thư phê duyệt`, path: '/approvals', permission: 'approvals:view' },
      { key: 'approval-templates', label: msg({ context: 'host', message: `Mẫu phê duyệt` }), path: '/approvals/templates', permission: 'approval_templates:view' },
    ],
  },

  // ===== Automation — single-link (KHÔNG có items, giống Reports) — clone 1:1 Core =====
  // LƯU Ý: KHÔNG để `items: []` — filter visibleMenu cuối loại group có items rỗng → mất menu.
  {
    key: 'automation',
    module: 'automation',
    label: msg`Automation`,
    icon: Zap,
    basePath: '/automation',
    path: '/automation',
  },

  // ===== Tài chính (Finance) — TẠM ẨN. Core có duy nhất Tên miền (466 LOC FE + 159 LOC BE),
  // SaaS chưa port → render placeholder gây UX gãy (Bug #5 2026-05-14). Document trong
  // docs/PORT_GAPS.md, bật lại khi port full module.
  // {
  //   key: 'finance',
  //   module: 'domains',
  //   label: msg`Tài chính`,
  //   icon: Landmark,
  //   basePath: '/finance',
  //   items: [
  //     { key: 'finance-domains', label: msg`Tên miền`, path: '/finance/domains' },
  //   ],
  // },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Cuối menu: Báo cáo → Tiện ích → Hệ thống (anh chốt thứ tự 2026-05-24).
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // ===== Kho báo cáo — ẨN KHỎI MENU 16/09/2026 (sếp Phú: *"ẩn luôn menu report"*).
  // Cùng cách với «Hỗ trợ» và «Tổng quan»: gỡ mục menu, GIỮ NGUYÊN route + page.
  //   · /reports, /reports/:module, /reports/:module/:key vẫn sống trong App.jsx — link cũ
  //     trong email/bookmark/nút «Báo cáo» của màn khác không gãy;
  //   · thẻ trong kho vẫn lọc qua HIDDEN_REPORT_MODULES + HIDDEN_REPORT_KEYS như cũ.
  // Bật lại = bỏ chú thích khối dưới (và trả `BarChart3` vào dòng import ở đầu tệp).
  // Core + Work SaaS VẪN có nhóm này ⇒ drift CÓ CHỦ Ý, ghi ở docs/PORT_GAPS.md.
  // {
  //   key: 'reports',
  //   label: msg`Báo cáo`,
  //   icon: BarChart3,
  //   path: '/reports',
  // },

  // ===== Tiện ích — Nhà cung cấp + Kho in ấn =====
  {
    key: 'utilities',
    module: 'utilities',
    label: msg`Tiện ích`,
    icon: Wrench,
    basePath: '/utilities',
    items: [
      { key: 'utilities-suppliers', label: msg({ context: 'host', message: `Nhà cung cấp` }), path: '/utilities/suppliers', permission: 'suppliers:view' },
      // Xuất file (Export Center) — SaaS-only tới khi port sang Core (chunk 4, docs/EXPORT_CENTER.md).
      // Đặt ở Tiện ích vì nó phục vụ MỌI module: không thuộc CRM/Nhân sự, cũng không phải khu admin
      // ("Hệ thống") vì nhân viên vẫn phải vào lấy file của chính mình.
      { key: 'utilities-exports', label: msg({ context: 'host', message: `Xuất file` }), path: '/utilities/exports', permission: 'exports:view' },
      { key: 'utilities-print-library', label: msg`Kho in ấn`, path: '/utilities/print-library', permission: 'print_library:view' },
      { key: 'utilities-noti-templates', label: msg({ context: 'host', message: `Mẫu thông báo` }), path: '/utilities/notification-templates', permission: 'noti_templates:view' },
      // Email Template — port 1:1 Core (adminMenu.js line 286): moved từ "Hệ thống" → "Tiện ích" (2026-07-03).
      // GIỮ path /settings/email-templates vì page shared cnv-ui (EmailTemplatesPage) hardcode nav
      // /settings/email-templates/:id — đổi path sẽ vỡ nav (giống tiền lệ int-bot).
      { key: 'email-templates', label: msg`Email Template`, path: '/settings/email-templates', permission: 'email_templates:view' },
      { key: 'utilities-company-room', label: msg({ context: 'host', message: `Nhóm chat chung` }), path: '/utilities/company-room', permission: 'company_room:view' },
      // Thư viện Pipeline — port 1:1 Core: moved từ "Hệ thống" → "Tiện ích" (2026-07-15).
      // Path /settings/pipelines đúng như Core (adminMenu.js:297). Trước 2026-08-04 SaaS để
      // /recruitment/pipelines với lý do "đổi path sẽ vỡ nav shared cnv-ui" — NGƯỢC: chính path
      // lệch đó làm vỡ nav, vì page shared điều hướng cứng sang /settings/pipelines/*. Route
      // /recruitment/pipelines/* vẫn giữ làm alias trong App.jsx (Core cũng mount cả hai).
      // ẨN 12/10/2026 (sếp Phú: «off ngay, vì mấy tính năng này ko có trên siteconnect») — thư viện chỉ phục vụ ba loại
      // pipeline Tuyển dụng / Deals / Ticket, cả ba cụm đã tắt ở Site Connect (cnvOnlyModulesOff + modules.* ở
      // config/tenant.js). Đo prod: không workspace nào có pipeline ngoài dòng gieo sẵn. Route chuyển về /dashboard.
      //   { key: 'system-pipelines', label: msg`Thư viện Pipeline`, path: '/settings/pipelines', permission: 'recruitment_pipelines:view' },
      // Giao diện Kanban — port từ cnvwork-saas 27/08/2026 (sếp Phú: bảng Kanban phải theo
      // chuẩn cài đặt chung của hệ thống). Đặt ở «Tiện ích» đúng như Work + Core (họ chuyển
      // từ «Hệ thống» sang đây 17/08). GIỮ path /settings/kanban-theme — page dùng chung.
      // Dùng lại quyền `settings`, KHÔNG đẻ module quyền mới (BE gate ghi bằng settings:update).
      { key: 'kanban-theme', label: msg`Giao diện Kanban`, path: '/settings/kanban-theme', permission: 'settings:view' },
      // Bot tenant (webhook gửi/nhận tin) — port 1:1 Core (adminMenu.js line 279):
      // nằm trong "Tiện ích", gate theo team_chat. GIỮ path /settings/bots (page
      // shared cnv-ui hardcode nav /settings/bots/:id).
      { key: 'int-bot', label: msg`Bot`, path: '/settings/bots', permission: 'int_bot:view', privateModule: 'team_chat' },
    ],
  },

  // ===== System — port 1:1 từ Core's adminMenu.js (line 274-307), filter Mattermost +
  // CNV-only items. SaaS thêm API Keys + Webhooks vào `Tích hợp`. =====
  {
    key: 'system',
    label: msg`Hệ thống`,
    icon: Settings,
    basePath: '/settings',
    items: [
      // Cài đặt
      { key: 'settings',          label: msg`Cài đặt chung`,     path: '/settings',                          group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'settings:view' },
      // Email Template ĐÃ CHUYỂN sang menu "Tiện ích" (port 1:1 Core adminMenu.js, 2026-07-03).
      // Menu app theo WORKSPACE (21/09/2026, sếp Phú) — CHỈ Connect có máy chủ cho trang này.
      // Dùng lại quyền `settings` (BE gate ghi bằng settings:update), không đẻ khoá quyền mới.
      { key: 'mobile-app',        label: msg`Ứng dụng di động`,  path: '/settings/mobile-app',               group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'settings:view' },
      { key: 'notification-logs', label: msg`Lịch sử thông báo`, path: '/settings/notification-logs',        group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'notification_logs:view' },
      { key: 'ai-usage',          label: msg({ context: 'host', message: `Chi phí AI` }),        path: '/settings/ai-usage',                 group: 'Cài đặt', groupLabel: msg`Cài đặt`, permission: 'ai_usage:view' },
      // Tích hợp — bên thứ 3 + SaaS-only (API Keys, Webhooks)
      // Zalo + Facebook: kết nối Page/OA đã có ở /inbox/channels — bỏ trang riêng,
      // SaaS dùng chung 1 App ở system-level (ENV), user không cần nhập key (2026-05-12)
      { key: 'int-claude',        label: msg`Claude`,            path: '/settings/integrations/claude',      group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'int_claude:view' },
      // Microsoft 365 / Entra bỏ khỏi CNV Connect (2026-08-16, sếp Phú chốt) — docs/PORT_GAPS.md.
      // Core + Work SaaS vẫn dùng. BE /digital-assets/integration GIỮ NGUYÊN.
      { key: 'int-chatgpt',       label: msg`ChatGPT`,           path: '/settings/integrations/chatgpt',     group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'int_chatgpt:view' },
      // Connecteam — CHỈ CÓ ở CNV Connect (Core + Work SaaS không dùng), nên item này là
      // drift CÓ CHỦ Ý so với adminMenu của Core. Quyền dùng lại `settings:view` thay vì đẻ
      // khoá `int_connecteam` mới: khoá mới phải seed cả ở cnv-ui (gói dùng chung 3 hệ) —
      // xem lý do đầy đủ ở đầu `api/src/modules/connecteam/routes/v1.js`.
      { key: 'int-connecteam',    label: msg`Connecteam`,        path: '/settings/integrations/connecteam',  group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'settings:view' },
      { key: 'google-settings',   label: msg`Google`,            path: '/settings/google',                   group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'int_google:view' },
      // Fireflies bỏ khỏi SaaS (2026-07-11) — Core attendance-app vẫn dùng.
      // CNV Call bỏ khỏi CNV Connect (2026-08-16, sếp Phú chốt) — docs/PORT_GAPS.md.
      // Core + Work SaaS vẫn dùng. BE /cnv-call/* + softphone giữ nguyên code.
      // CNV Chat bỏ khỏi SaaS (2026-07-25) — SaaS tự đăng ký tenant với chat-service khi
      // provisioning, không cần trang setup thủ công. Core attendance-app vẫn dùng.
      // Bot ĐÃ CHUYỂN sang group "Tiện ích" (port 1:1 Core adminMenu.js).
      // API Keys + Webhooks ẨN 12/10/2026 (cùng lệnh tắt cụm CNV-only của sếp Phú): mọi scope API key là hộp thư / AI / KB /
      // CRM và mọi sự kiện webhook là hộp thư / CRM — đều đã tắt ở Site Connect (máy chủ trả 404 module_off). Đo prod:
      // 0 API key, 0 webhook. Route chuyển về /dashboard; máy chủ /api-keys, /webhooks giữ nguyên (Console vẫn quản).
      //   { key: 'api-keys',          label: msg`API Keys`,          path: '/api-keys',                          group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'api_keys:view' },
      //   { key: 'webhooks',          label: msg`Webhooks`,          path: '/webhooks',                          group: 'Tích hợp', groupLabel: msg({ context: 'host', message: `Tích hợp` }), permission: 'webhooks:view' },
      // Hệ thống
      { key: 'roles',             label: msg({ context: 'host', message: `Vai trò` }),           path: '/roles',                             group: 'Hệ thống', groupLabel: msg`Hệ thống`, permission: 'roles:view' },
      { key: 'permissions',       label: msg`Phân quyền`,        path: '/permissions',                       group: 'Hệ thống', groupLabel: msg`Hệ thống`, permission: 'permissions:view' },
      { key: 'storage-usage',     label: msg({ context: 'host', message: `Dung lượng` }),        path: '/settings/storage',                  group: 'Hệ thống', groupLabel: msg`Hệ thống`, permission: 'storage:view_all' },
      // Mục «Gói & Thanh toán» ĐÃ CHUYỂN sang nhóm riêng ở cuối tệp (29.08.2026): xem gói,
      // lịch sử đợt mua và nâng cấp giờ nằm CHUNG một trang kernel, không còn hai chỗ rời nhau.
      // Thư viện Pipeline ĐÃ CHUYỂN sang menu "Tiện ích" (port 1:1 Core adminMenu.js, 2026-07-15).
      // SaaS-only — 1 user thuộc nhiều workspace. Core không có vì single-tenant.
      { key: 'switch-workspace',  label: msg`Đổi workspace`,     path: '/select-workspace',                  group: 'Hệ thống', groupLabel: msg`Hệ thống` },
    ],
  },

  // ===== Marketplace — SaaS-ONLY (Core không có). Marketplace module = catalog tự catalog =====
  // Documented trong docs/PORT_GAPS.md
  {
    key: 'marketplace',
    module: 'marketplace',
    // Trang này gom cả phần Billing vào nên "Marketplace"/"Kho ứng dụng" không còn đúng việc nó
    // làm. Nhãn thanh dọc chỉ vừa ~10 KÝ TỰ — "Gói & Thanh toán" bị cắt cụt, nên thanh dọc để
    // NGẮN, tên đầy đủ nằm ở mục con + breadcrumb + tiêu đề trang.
    label: msg`Gói dịch vụ`,
    icon: CreditCard,
    basePath: '/billing',
    items: [
      { key: 'marketplace-modules', label: msg({ context: 'host', message: `Gói & Thanh toán` }), path: '/billing', permission: 'marketplace:view' },
    ],
  },
];

// Routes ẩn secondary submenu — khi match thì secondary sidebar 240px ẨN, chỉ giữ
// primary rail 80px. Match Core's AdminSidebar.DETAIL_PATTERNS (filter routes CNV-only).
//
// Pattern của /inbox, /campaigns/:id, /recruitment/c/, /crm/* đã gỡ cùng 4 module bị bỏ
// (App.jsx redirect các path đó về /dashboard nên không route nào match nữa).
// /pipelines/* GIỮ: Thư viện Pipeline nằm ở Tiện ích, ticket vẫn dùng.
const DETAIL_PATTERNS = [
  /\/pipelines\/[^/]+$/,
  /\/pipelines\/new$/,
  /\/tasks\/projects\/[^/]+$/,
  /^\/approvals\/templates\/(new|[a-zA-Z0-9-]+)$/,
  /^\/approvals\/[a-f0-9-]{8,}$/i,
  /^\/me\/?$/,                                       // personal settings — ẩn submenu
  /^\/billing\/?$/,                                  // Gói & Thanh toán — submenu 1 item, ẩn cho gọn
  /^\/marketplace\/?$/,                              // đường dẫn CŨ, còn sống để link đã gửi không chết
  // ⛔ Cụm Dự án + Xếp việc KHÔNG còn ẩn nav phụ (03/09/2026).
  //
  // Trước đó chúng nằm trong danh sách này vì 27/08 sếp Phú tắt nav phụ cho cụm Dự án —
  // "submenu đang khá vướng" — và bù lại bằng dãy icon chuyển màn ngay trên trang. Đến 03/09
  // dãy ấy bị bỏ hẳn ("bỏ luôn vì submenu đã có, xem lại để làm cho đúng chuẩn thiết kế hệ
  // thống đang có"), nên giữ dòng ẩn này là mất CẢ HAI đường: không dãy, không menu con —
  // vào /projects rồi không cách nào sang Bảng giai đoạn hay Bản đồ ngoài gõ tay URL.
  //
  // Nay theo đúng chuẩn cụm Nhân sự: menu con luôn mở ở thanh trái lo điều hướng.
];

// Fullscreen: ẩn cả 2 sidebar tier (chỉ primary 80px), content fixed inset.
const FULLSCREEN_PATTERNS = [
  /^\/approvals\/templates\/(new|[a-zA-Z0-9-]+)$/,   // template builder
  // Trang soạn biểu mẫu dự án mượn ĐÚNG khuôn của template builder ngay trên (sếp Phú
  // 03/09: *"tham khảo mẫu phê duyệt mà làm cho đúng"*). Khuôn đó chỉ thành hình khi vỏ
  // app cho trang chiếm trọn màn: thiếu dòng này thì `FormTemplateEditor asPage` nằm
  // trong vỏ `px-8 pt-8`, nền slate-50 co thành một mảng lọt giữa chứ không ra nền
  // trang — đúng hiện tượng "trang này lại ko có background gì".
  /^\/projects\/form-templates\/(new|[a-zA-Z0-9-]+)$/,
  /^\/approvals\/[a-f0-9-]{8,}$/i,                    // approval detail
  /^\/me\/?$/,                                        // personal settings
  /^\/automation\/?$/,
  /^\/automation\/[a-zA-Z0-9-]+$/,
  /^\/chat(\/.*)?$/,                                  // team chat — Slack-style full page
  // /tickets/:id + /support/:id gỡ 27/08/2026 cùng cụm Hỗ trợ — App.jsx chuyển hết về
  // /dashboard nên không còn trang nào để mở toàn màn hình.
];

// Fullbleed: content sát top + full width (no padding), nhưng VẪN giữ 2-tier sidebar.
// Page tự handle header sticky. Khớp Core's isApprovalsFullBleed.
// Core FULLBLEED chỉ /tasks(/.*) + /approvals/... — KHÔNG có /crm.
// /crm KHÔNG fullbleed: dùng padding chuẩn (px-8 pt-8), tránh body tràn đụng secondary sidebar (240px).
const FULLBLEED_PATTERNS = [
  /^\/approvals\/?$/,                                 // inbox + sub-tabs
  /^\/approvals\/(inbox|templates|report|shared)\/?$/,
  /^\/tasks\/?$/,
  /^\/tasks\/board$/,
];

export default function AppLayout() {
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  // Private module gate: filter menu groups có `privateModule` — chỉ hiện khi
  // tenant.private_modules[name] = true. Match BE middleware requirePrivateModule.
  const privateModules = activeTenant?.private_modules || {};
  // Permission gate (2026-05-27): filter từng item theo `item.permission`.
  // Item KHÔNG có `permission` → ai cũng thấy (default).
  // Group hết items sau filter → bỏ luôn group (tránh hiển thị icon group rỗng).
  const visibleMenu = NAV_GROUPS
    .filter((g) => !g.privateModule || privateModules[g.privateModule] === true)
    .map((g) => {
      // Group single-link (`path`, không `items`): gate nằm ở CẤP NHÓM. Group nào không khai
      // `permission` (vd Automation) thì giữ nguyên như trước — ai cũng thấy.
      // (Chép 1:1 bản Work SaaS — trước 16/09/2026 Connect chỉ soi `g.items` nên nhóm link
      //  đơn lọt mọi quyền.)
      if (!Array.isArray(g.items)) return (g.permission && !userHasPermission(user, g.permission)) ? null : g;
      // Item gate: permission + (optional) privateModule per-item (vd Bot gate
      // theo team_chat — mirror Core adminMenu.js).
      const items = g.items.filter((it) =>
        userHasPermission(user, it.permission)
        && (!it.privateModule || privateModules[it.privateModule] === true));
      return { ...g, items };
    })
    // Giữ single-link group (có `path`, vd Automation/Xếp việc) dù không có items; chỉ loại
    // group HAD items nhưng bị permission-filter hết → tránh icon group rỗng.
    // `g && ...`: nhánh single-link phía trên trả null khi thiếu quyền — thiếu chốt này là
    // đọc `.path` của null ⇒ TRẮNG TRANG cả app với user không có quyền (bản thân admin
    // thì không bao giờ gặp).
    .filter((g) => g && (g.path || !Array.isArray(g.items) || g.items.length > 0));

  // Badge counters — gộp polling 1 endpoint /badges/summary + realtime team_chat unread.
  // poll 180s = LƯỚI AN TOÀN (time-based overdue + event lỡ); realtime (useBadgeRealtime) lo
  // refresh TỨC THÌ khi server emit badge:refresh (task/approval/ticket... assign cho user).
  // Prefetch nền mọi chunk route sau khi vào app → bấm menu hết spinner "loading".
  useEffect(() => { prefetchAllRoutes(); }, []);

  useBadgeRealtime();
  const polledBadges = useSidebarBadges(SIDEBAR_BADGE_ENDPOINTS, { api, pollIntervalMs: 180_000 });
  const teamChatUnread = useTeamChatUnreadTotal();
  const badgeCounts = { ...polledBadges, team_chat: teamChatUnread };

  // ── Giao diện v2 ────────────────────────────────────────────────────────────
  // `data-v2-shell` trên <body> là cổng của MỌI rule vỏ trong @cnv/ui/styles/ui-v2.css.
  // Đặt ở đây (chứ không phải trên <html>) để trang NGOÀI layout — đăng nhập, nhận lời mời,
  // chọn workspace, link chia sẻ công khai — không bị lớp phủ vỏ chạm tới dù cờ v2 đang bật.
  // Gate tenant là CHỐT CHẶN CỨNG, đứng trước lựa chọn của user: tenant ngoài allowlist
  // không bao giờ thấy v2, kể cả khi localStorage còn sót ui_version='v2'.
  const tenantAllowed = isTenantAllowed(activeTenant?.slug);
  const [uiChoice, setUiChoice] = useState(() => readUiVersion());
  useEffect(() => subscribeUiVersion(setUiChoice), []);
  const isV2 = tenantAllowed && uiChoice === UI_V2;
  // activeTenant về SAU lần render đầu (từ /auth/me) → phải áp lại attribute khi biết tenant,
  // nếu không thì tenant không được phép vẫn giữ data-ui mà IIFE boot đã set từ slug cũ
  // (vd user vừa đổi workspace trong cùng tab).
  useEffect(() => { applyUiVersion(isV2 ? UI_V2 : UI_V1); }, [isV2]);
  useEffect(() => {
    document.body.setAttribute('data-v2-shell', '');
    return () => document.body.removeAttribute('data-v2-shell');
  }, []);

  // Route guard quyền (2026-07-15) — derive từ chính NAV_GROUPS nên không lệch menu.
  // Dùng NAV_GROUPS gốc (KHÔNG phải visibleMenu đã lọc): visibleMenu bỏ item user không có quyền
  // → path đó biến mất khỏi map → checkRoutePermission fail-closed sẽ chặn NHẦM vì "không khớp"
  // thay vì vì "thiếu quyền". Cùng kết quả ở đây, nhưng sai lý do và sẽ vỡ khi nới rule sau này.
  const routeMap = useMemo(() => buildRouteMap(NAV_GROUPS), []);
  const isAdmin = user?.role === 'admin' || (user?.permissions || []).includes('*');
  const allowed = checkRoutePermission(
    location.pathname, routeMap, (p) => userHasPermission(user, p), isAdmin,
  );

  // ⚠️ Đặt SAU toàn bộ hook — early-return trước hook là vi phạm Rules of Hooks (lint:hooks chặn).
  // BE mới là chốt chặn thật (requirePermission); đây chỉ tránh user gõ URL vào trang rồi ăn 403.
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const brand = {
    // Custom tenant logo nếu admin upload (lưu meta.tenants.metadata.branding.logo_url).
    // Fallback là thương hiệu của chính cụm này — KHÔNG phải 'CNV Work' (bản clone để sót,
    // và ở v2 tên này nằm ngay trên thanh đầu trang nên sai là thấy ở mọi màn hình).
    logo: activeTenant?.branding?.logo_url || '/siteconnect.png',
    appName: activeTenant?.branding?.app_name || 'Site Connect',
    appShort: activeTenant?.branding?.app_short || 'SC',
    subtitle: activeTenant?.display_name,
  };

  return (
    <>
      {isV2 && <TopBarV2 menu={visibleMenu} brand={brand} />}
      {/* v1 không có thanh trên cùng → pill nổi là lối DUY NHẤT để bật lại v2.
          Chỉ hiện cho tenant trong allowlist: tenant khác không được thấy dấu vết nào của v2. */}
      {!isV2 && tenantAllowed && <UiVersionSwitch floating />}
      <AppLayoutShared
        menu={visibleMenu}
        brand={brand}
        detailPatterns={DETAIL_PATTERNS}
        fullscreenPatterns={FULLSCREEN_PATTERNS}
        fullbleedPatterns={FULLBLEED_PATTERNS}
        customSecondary={CUSTOM_SECONDARY}
        badgeCounts={badgeCounts}
        // Đáy menu trái. v1: avatar → popup cá nhân (đổi ngôn ngữ / cài đặt / đăng xuất) —
        // không truyền thì avatar nhảy thẳng /me, muốn đổi ngôn ngữ phải lần 3 bước.
        // v2: RailBottomV2 = công tắc "về giao diện cũ" + chính avatar đó cho khổ < 1024px
        // (desktop v2 đã có avatar trên thanh đầu trang).
        primaryBottomSlot={isV2 ? <RailBottomV2 /> : <UserMenu />}
        showChatDock
      />
    </>
  );
}
